// Smart Sash Admin Panel - password-change flow state
//
// HS-847 P1.3: this module no longer manages tokens. Admin authenticates with HttpOnly
// cookies (`accessToken`/`refreshToken`, set by the backend), and the shared
// `@smartsash/auth` `TokenStore` owns any in-app token state — so the token half here
// (saveTokens / getAccessToken / getRefreshToken / getTokens / isTokenExpired /
// hasValidTokens / getValidToken) had zero callers outside its own dead helpers and the
// three `sessionStorage` keys it used were never written by anything else.
//
// What remains is the one thing the shared package has no equivalent for: resuming a
// public flow across the reload that a required password change (HTTP 301) causes.
// `@smartsash/auth` keeps its `flowToken` in memory only, so this survives the reload.
// The `tokenManager` name and `clearTokens` are kept to avoid churning 8 logout call
// sites; renaming to `passwordChangeFlowStore` rides the auth slice (P2).

const PASSWORD_CHANGE_REQUIRED_KEY = 'smart_sash_password_change_required';
const PASSWORD_CHANGE_FLOW_KEY = 'smart_sash_password_change_flow';

export type PasswordChangeFlowType = 'rfid-assignment' | 'device-registration';
export type PasswordChangeNextStep = 'activating' | 'lab-selection';

export interface PasswordChangeFlowState {
  flowType: PasswordChangeFlowType;
  /**
   * The MATCH KEY the resuming hook compares against, deliberately built from the sessionId
   * alone so it survives the institutionId/tenantId param rename. It is not a URL to navigate to.
   */
  redirectPath: string;
  /**
   * Where to actually send the browser after the password change (HS-1225).
   *
   * `redirectPath` cannot do this job: it omits `institutionId`, which `/rfid-assignment`
   * requires, so navigating to it lands on "institution missing". Nothing navigated to it before,
   * because the flow dead-ended at `/login` and was never resumed.
   */
  returnPath: string;
  login: string;
  nextStep: PasswordChangeNextStep;
}

const isPasswordChangeFlowState = (value: unknown): value is PasswordChangeFlowState => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Record<string, unknown>;

  return (
    (candidate.flowType === 'rfid-assignment' || candidate.flowType === 'device-registration') &&
    typeof candidate.redirectPath === 'string' &&
    typeof candidate.returnPath === 'string' &&
    typeof candidate.login === 'string' &&
    (candidate.nextStep === 'activating' || candidate.nextStep === 'lab-selection')
  );
};

export const tokenManager = {
  /**
   * Mark whether the current session must change password before normal access.
   */
  setPasswordChangeRequired: (required: boolean): void => {
    if (required) {
      sessionStorage.setItem(PASSWORD_CHANGE_REQUIRED_KEY, 'true');
      return;
    }

    sessionStorage.removeItem(PASSWORD_CHANGE_REQUIRED_KEY);
  },

  /**
   * Check whether the current session requires a password change.
   */
  requiresPasswordChange: (): boolean => {
    return sessionStorage.getItem(PASSWORD_CHANGE_REQUIRED_KEY) === 'true';
  },

  /**
   * Persist the public flow that should resume after a required password change.
   */
  setPasswordChangeFlow: (flow: PasswordChangeFlowState | null): void => {
    if (!flow) {
      sessionStorage.removeItem(PASSWORD_CHANGE_FLOW_KEY);
      return;
    }

    sessionStorage.setItem(PASSWORD_CHANGE_FLOW_KEY, JSON.stringify(flow));
  },

  /**
   * Read the public flow that should resume after password change.
   */
  getPasswordChangeFlow: (): PasswordChangeFlowState | null => {
    const rawValue = sessionStorage.getItem(PASSWORD_CHANGE_FLOW_KEY);
    if (!rawValue) {
      return null;
    }

    try {
      const parsedValue: unknown = JSON.parse(rawValue);
      if (isPasswordChangeFlowState(parsedValue)) {
        return parsedValue;
      }
    } catch {
      // Invalid persisted flow state should not block the user.
    }

    sessionStorage.removeItem(PASSWORD_CHANGE_FLOW_KEY);
    return null;
  },

  /**
   * Drop all password-change flow state. Called on logout and when a flow completes.
   */
  clearTokens: (): void => {
    sessionStorage.removeItem(PASSWORD_CHANGE_REQUIRED_KEY);
    sessionStorage.removeItem(PASSWORD_CHANGE_FLOW_KEY);
  },
};

export default tokenManager;
